import React, { useCallback, useState } from 'react';
import { AlertCircle, FileCheck, Upload, Loader2, Eye, Edit2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { DocumentViewer } from '../common/DocumentViewer';
import { useUploadProfessionalDocuments } from '../../hooks/useUploadProfessionalDocuments';
import { useDeleteProfessionalDocument } from '../../hooks/useDeleteProfessionalDocument';

interface DoctorDocumentsUploadProps {
  doctorId: string;
  hasDocuments?: boolean;
  documentUrl?: string;
  onDocumentUploaded?: () => void;
}

export const DoctorDocumentsUpload: React.FC<DoctorDocumentsUploadProps> = ({
  doctorId,
  hasDocuments = false,
  documentUrl,
  onDocumentUploaded,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { uploadDocuments, isLoading } = useUploadProfessionalDocuments(doctorId);
  const { deleteDocument, isLoading: isDeleteLoading } = useDeleteProfessionalDocument(doctorId);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];

    if (file.size > maxSize) {
      return { valid: false, error: 'Arquivo muito grande. Máximo 10MB.' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Formato inválido. Aceita PDF, PNG ou JPG.' };
    }

    return { valid: true };
  };

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const file = files[0];
      const validation = validateFile(file);
      
      if (!validation.valid) {
        alert(validation.error);
        return;
      }

      setSelectedFile(file);
    }
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.currentTarget.files || []);
    if (files.length > 0) {
      const file = files[0];
      const validation = validateFile(file);
      
      if (!validation.valid) {
        alert(validation.error);
        e.currentTarget.value = '';
        return;
      }

      setSelectedFile(file);
    }
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) return;

    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + Math.random() * 30;
      });
    }, 500);

    const success = await uploadDocuments(selectedFile);
    
    clearInterval(progressInterval);
    
    if (success) {
      setUploadProgress(100);
      setSelectedFile(null);
      setIsEditMode(false);
      setTimeout(() => setUploadProgress(0), 1000);
      onDocumentUploaded?.();
    }
  };

  const handleDeleteDocument = async () => {
    if (!window.confirm('Tem certeza que deseja deletar este documento? Será necessário fazer um novo upload para aprovação.')) {
      return;
    }

    setIsDeleting(true);
    const success = await deleteDocument();
    
    if (success) {
      setIsEditMode(false);
      onDocumentUploaded?.();
    }
    setIsDeleting(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          {hasDocuments ? (
            <FileCheck className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Documentos Profissionais</h3>
            <p className="text-sm text-gray-600 mt-1">
              {hasDocuments
                ? 'Seus documentos foram enviados e estão pendentes de análise do administrador.'
                : 'Você deve enviar seus documentos profissionais (CRM, certificados, etc) antes que sua conta seja aprovada.'}
            </p>
          </div>
        </div>
      </div>

      {hasDocuments && !isEditMode && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-green-800 font-medium">✓ Documentos Enviados</span>
            <div className="flex items-center gap-2">
              {documentUrl && (
                <button
                  onClick={() => setShowDocumentViewer(true)}
                  className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1 px-3 py-1 hover:bg-green-100 rounded transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  Ver
                </button>
              )}
              <button
                onClick={() => setIsEditMode(true)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 px-3 py-1 hover:bg-blue-100 rounded transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Editar
              </button>
              <button
                onClick={handleDeleteDocument}
                disabled={isDeleteLoading || isDeleting}
                className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1 px-3 py-1 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
              >
                {isDeleting || isDeleteLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deletando
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Deletar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditMode && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800 font-medium mb-3">
            📝 Modo Edição: Selecione um novo documento para substituir o anterior
          </p>
          <button
            onClick={() => {
              setIsEditMode(false);
              setSelectedFile(null);
            }}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Cancelar edição
          </button>
        </div>
      )}

      {(!hasDocuments || isEditMode) && (
        <>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragOver
                ? 'border-teal-600 bg-teal-50'
                : 'border-gray-300 bg-gray-50'
            }`}
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-600 mb-2">
              Arraste seus documentos aqui ou{' '}
              <label className="text-teal-600 hover:text-teal-700 font-semibold cursor-pointer">
                clique para selecionar
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileInputChange}
                />
              </label>
            </p>
            <p className="text-xs text-gray-500">
              PDF, PNG ou JPG • Máximo 10MB
            </p>
          </div>

          {selectedFile && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{selectedFile.name}</p>
                  <p className="text-xs text-gray-600">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                {!isLoading && (
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>

              {isLoading && (
                <>
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-700">
                        {isEditMode ? 'Atualizando documento...' : 'Enviando arquivo...'}
                      </span>
                      <span className="text-xs font-semibold text-teal-600">
                        {Math.min(Math.round(uploadProgress), 100)}%
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-teal-600 transition-all duration-300"
                        style={{ width: `${Math.min(uploadProgress, 100)}%` }}
                      />
                    </div>
                  </div>
                </>
              )}

              <Button
                onClick={handleUpload}
                disabled={isLoading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isEditMode ? 'Atualizando...' : 'Enviando...'}
                  </>
                ) : (
                  <>
                    <FileCheck className="w-4 h-4 mr-2" />
                    {isEditMode ? 'Atualizar Documento' : 'Enviar Documentos'}
                  </>
                )}
              </Button>
            </div>
          )}
        </>
      )}

      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-xs text-gray-600 font-semibold mb-2">DOCUMENTOS ACEITOS:</p>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>• Cópia do CRM oficial</li>
          <li>• Certificado de especialidade (se houver)</li>
          <li>• Comprovante de inscrição no conselho profissional</li>
          <li>• Outros certificados profissionais relevantes</li>
        </ul>
      </div>

      {import.meta.env.VITE_ENABLE_DEBUG && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
          <p className="font-semibold mb-1">🔧 Debug Info (Remover em Produção):</p>
          <p>Cloud Name: {import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || '❌ Not set'}</p>
          <p>Upload Preset: {import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || '❌ Not set'}</p>
        </div>
      )}

      <DocumentViewer
        fileName="Documentos Profissionais"
        isOpen={showDocumentViewer}
        onClose={() => setShowDocumentViewer(false)}
        doctorId={doctorId}
      />
    </div>
  );
};
